import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Icon } from '@/components/Icon';
import type { AppGroup, AppManifest } from '@/apps/types';
import { cascadeIntent } from './cascade';
import type { CascadeRow } from './cascade';
import { useStartMenuItems } from './useStartMenuItems';
import css from './StartMenu.module.css';

/**
 * How long the pointer must rest on a folder before its submenu opens, and how
 * long any pending change waits before it takes effect.
 *
 * 400ms is the number Windows XP itself used: `MenuShowDelay` under
 * `HKCU\Control Panel\Desktop` shipped at 400, and the Start menu's cascades
 * were driven by it. The application menu bars here use 250 — `DropdownMenu`'s
 * `SUBMENU_DELAY` — because a menu bar is a tighter target you drive straight
 * down; a cascade you approach diagonally needs the extra patience, and it is
 * that patience which makes the diagonal work at all.
 */
const MENU_SHOW_DELAY = 400;

export interface AllProgramsFlyoutProps {
  /** Launch a program and dismiss the whole Start menu. */
  onLaunch: (appId: string) => void;
}

/**
 * The panel that flies out to the right of the Start menu's white column:
 * ungrouped programs first, then one expandable folder per manifest group.
 *
 * ── Why every change to `openGroup` is on a timer ───────────────────────────
 * A folder's submenu hangs off the folder's own row, so the natural path to an
 * item halfway down it is a diagonal — and that diagonal clips the folder row
 * *below* on its way past. Measured: from the middle of the Games row to the
 * middle of Solitaire, the corridor that misses the Internet row is about 1.5px
 * wide. Reacting to hover immediately therefore means swapping the submenu out
 * from under a pointer that was on its way into it.
 *
 * So nothing here acts on hover directly. Entering a row *schedules* a change,
 * and anything the pointer settles on can cancel a pending one — arriving in
 * the open submenu cancels first of all. That is the same contract
 * `DropdownMenu` holds for the application menus, and it is the reason a
 * diagonal survives.
 */
export function AllProgramsFlyout({ onLaunch }: AllProgramsFlyoutProps) {
  const { loose, groups } = useStartMenuItems();
  const [openGroup, setOpenGroup] = useState<AppGroup | null>(null);
  const timerRef = useRef<number | null>(null);

  const clearTimer = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // This component unmounts the moment All Programs closes, so a pending timer
  // needs no cleanup beyond this — and `openGroup` resets for free, which is
  // why it lives here rather than up in `StartMenu`.
  useEffect(() => clearTimer, []);

  /** Hover intent: act on this only if the pointer is still interested in 400ms. */
  const schedule = (group: AppGroup | null) => {
    clearTimer();
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      setOpenGroup(group);
    }, MENU_SHOW_DELAY);
  };

  /** A click is not a guess about intent, so it takes effect at once. */
  const openNow = (group: AppGroup | null) => {
    clearTimer();
    setOpenGroup(group);
  };

  /**
   * Hovering anything in the cascade. `cascade.ts` decides what it means; all
   * that is left here is when to act on it, which is always `MENU_SHOW_DELAY`
   * later except for cancelling, which is immediate.
   */
  const onRowEnter = (row: CascadeRow) => {
    const intent = cascadeIntent(row, openGroup);
    if (intent.kind === 'cancel') clearTimer();
    else if (intent.kind === 'open') schedule(intent.group);
    else schedule(null);
  };

  /**
   * One program. `inSubmenu` is the whole difference between a row that closes
   * the open folder and a row that protects it — see `cascade.ts`.
   */
  const programRow = (app: AppManifest, inSubmenu = false) => (
    <li key={app.id} role="none">
      <button
        type="button"
        role="menuitem"
        className={css.row}
        onMouseEnter={() => onRowEnter({ group: null, inSubmenu })}
        onClick={() => onLaunch(app.id)}
      >
        <Icon src={app.icon} size={24} />
        <span className={css.rowLabel}>{app.name}</span>
      </button>
    </li>
  );

  return (
    <ul className={css.flyout} role="menu" aria-label="All Programs">
      {/* Arrow functions, not bare `programRow`: `map` passes the index as the
          second argument, which would make every row after the first think it
          was inside a submenu. */}
      {loose.map((app) => programRow(app))}

      {loose.length > 0 && groups.length > 0 ? (
        <li className={css.flyoutRule} role="separator" />
      ) : null}

      {groups.map((group) => {
        const expanded = openGroup === group.name;
        return (
          <li key={group.name} className={css.groupItem} role="none">
            <button
              type="button"
              role="menuitem"
              className={clsx(css.row, expanded && css.rowOpen)}
              aria-haspopup="true"
              aria-expanded={expanded}
              onMouseEnter={() => onRowEnter({ group: group.name, inSubmenu: false })}
              onClick={() => openNow(expanded ? null : group.name)}
            >
              <Icon src={expanded ? '/assets/icons/folder-open.svg' : '/assets/icons/folder.svg'} size={24} />
              <span className={css.rowLabel}>{group.name}</span>
              <span className={css.submenuArrow} aria-hidden="true" />
            </button>

            {expanded ? (
              <ul
                className={clsx(css.flyout, css.submenu)}
                role="menu"
                aria-label={group.name}
                // The panel's own chrome counts as arriving: a pointer that
                // lands on the border or the 2px padding, rather than cleanly
                // on a row, has still reached the submenu.
                onMouseEnter={clearTimer}
              >
                {group.apps.map((app) => programRow(app, true))}
              </ul>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
